
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Puzzle, Target, Users, BookOpen, Brain, Gamepad2, Sparkles, Zap } from "lucide-react";
import { WorksheetType, Worksheet } from "@/types/worksheet";
import { generateWorksheet } from "@/services/worksheetGeneratorService";

interface WorksheetTypeSelectorProps {
  grade: string;
  onTypeSelect: (type: string, subject: string, topic: string) => Promise<void>;
  isGenerating: boolean;
}

const worksheetTypes: WorksheetType[] = [
  {
    id: 'puzzle',
    name: 'AI Puzzle Games',
    description: 'Smart word searches, crosswords, and brain teasers generated just for you',
    icon: 'puzzle',
    category: 'puzzle',
    minGrade: 1,
    maxGrade: 9
  },
  {
    id: 'matching',
    name: 'AI Matching Games',
    description: 'Intelligent matching activities that adapt to your learning level',
    icon: 'target',
    category: 'matching',
    minGrade: 1,
    maxGrade: 9
  },
  {
    id: 'sorting',
    name: 'AI Sorting Activities',
    description: 'Dynamic categorization exercises tailored to your grade',
    icon: 'users',
    category: 'sorting',
    minGrade: 1,
    maxGrade: 9
  }
];

const WorksheetTypeSelector = ({ grade, onTypeSelect, isGenerating }: WorksheetTypeSelectorProps) => {
  const [isGeneratingType, setIsGeneratingType] = useState<string | null>(null);
  const [availableSubjects] = useState(['Math', 'Reading', 'Science']);

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'puzzle': return <Puzzle className="h-6 w-6" />;
      case 'target': return <Target className="h-6 w-6" />;
      case 'users': return <Users className="h-6 w-6" />;
      default: return <BookOpen className="h-6 w-6" />;
    }
  };

  const handleGenerateWorksheet = async (type: WorksheetType, subject: string) => {
    const key = `${type.id}-${subject}`;
    setIsGeneratingType(key);
    
    try {
      await onTypeSelect(type.category, subject, '');
    } catch (error) {
      console.error('Failed to generate worksheet:', error);
    } finally {
      setIsGeneratingType(null);
    }
  };

  const filteredTypes = worksheetTypes.filter(type => {
    const gradeNum = parseInt(grade || '1');
    return gradeNum >= type.minGrade && gradeNum <= type.maxGrade;
  });

  const getGradeMessage = () => {
    const gradeNum = parseInt(grade);
    if (gradeNum <= 3) {
      return "🌟 AI creates perfect activities for early learners! Building foundational skills with fun, personalized content.";
    } else if (gradeNum <= 6) {
      return "🚀 AI-powered worksheets designed to develop critical thinking and problem-solving skills at your level!";
    } else {
      return "🎯 Advanced AI-generated activities that challenge and enhance analytical thinking for higher grades!";
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <div className="inline-flex items-center justify-center mb-4 px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-full">
            <Sparkles className="h-4 w-4 mr-2 text-purple-600" />
            <span className="text-purple-700 font-medium text-sm">AI-Powered Learning</span>
            <Zap className="h-4 w-4 ml-2 text-blue-600" />
          </div>
          
          <h2 className="text-3xl font-bold mb-4 text-gray-800">Choose Your AI Activity</h2>
          <p className="text-gray-600 mb-4">Our AI creates unique, personalized worksheets just for you!</p>
          
          <Badge variant="secondary" className="text-lg px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold">
            Grade {grade} • AI Generated
          </Badge>
        </motion.div>
        
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-sm text-gray-600 bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded-lg border border-green-200"
        >
          {getGradeMessage()}
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {filteredTypes.map((type, index) => (
          <motion.div
            key={type.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
          >
            <Card className="border-2 border-blue-200 hover:border-purple-400 transition-all duration-300 h-full hover:shadow-lg transform hover:scale-105">
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-3">
                  <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full text-white">
                    {getIcon(type.icon)}
                  </div>
                </div>
                <CardTitle className="text-xl text-blue-700 mb-2">{type.name}</CardTitle>
                <p className="text-gray-600 text-sm leading-relaxed">{type.description}</p>
                
                <div className="flex justify-center mt-3">
                  <Badge variant="outline" className="text-xs px-3 py-1 bg-purple-50 text-purple-700 border-purple-200">
                    <Brain className="h-3 w-3 mr-1" />
                    AI Generated
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {availableSubjects.map((subject) => (
                    <div key={subject} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                      <span className="text-sm font-medium text-gray-700 flex items-center">
                        <BookOpen className="h-4 w-4 mr-2 text-gray-500" />
                        {subject}
                      </span>
                      <Button
                        size="sm"
                        onClick={() => handleGenerateWorksheet(type, subject)}
                        disabled={isGeneratingType === `${type.id}-${subject}` || isGenerating}
                        className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium"
                      >
                        {isGeneratingType === `${type.id}-${subject}` ? (
                          <>
                            <Brain className="h-4 w-4 mr-1 animate-spin" />
                            AI Creating...
                          </>
                        ) : (
                          <>
                            <Zap className="h-4 w-4 mr-1" />
                            Generate
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl p-6"
      >
        <div className="flex items-start gap-4">
          <div className="p-2 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex-shrink-0">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-yellow-800 font-bold text-lg mb-3">
              🤖 How AI Creates Your Perfect Worksheet:
            </p>
            <ul className="text-yellow-700 space-y-2">
              <li className="flex items-start">
                <span className="mr-3 text-orange-500 font-bold">1.</span>
                <span><strong>Choose your activity type</strong> - Pick what kind of learning you want to do</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-orange-500 font-bold">2.</span>
                <span><strong>Select your subject</strong> - Math, Reading, or Science</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-orange-500 font-bold">3.</span>
                <span><strong>AI generates unique content</strong> - Perfectly matched to Grade {grade} curriculum</span>
              </li>
              <li className="flex items-start">
                <span className="mr-3 text-orange-500 font-bold">4.</span>
                <span><strong>Learn and have fun!</strong> - Every worksheet is different and challenging</span>
              </li>
            </ul>
            <div className="mt-4 p-3 bg-white/60 rounded-lg border border-yellow-300">
              <p className="text-yellow-800 text-sm font-medium">
                ✨ <strong>Smart Adaptation:</strong> Our AI considers your grade level, subject knowledge, and learning patterns to create the perfect challenge every time!
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default WorksheetTypeSelector;
